import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Sparkles, Plus, Search, Trash2, Copy, Calendar, TrendingUp, Image as ImageIcon, ShieldCheck, MoreVertical,
  MessageSquare, Building2, Share2, Download,
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import Navbar from '../components/Navbar';
import api from '../lib/api';
import { useAuth } from '../context/AuthContext';
import { toast } from '../hooks/use-toast';
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger,
} from '../components/ui/dropdown-menu';
import AdDetailModal from '../components/AdDetailModal';

const Dashboard = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [ads, setAds] = useState([]);
  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(true);
  const [openAd, setOpenAd] = useState(null);  // saved-ad detail modal
  const [businessInfo, setBusinessInfo] = useState({ profiles: [], limit: 1, used: 0 });
  const [trialUsage, setTrialUsage] = useState(null);  // null until /billing/usage resolves

  const fetchAds = async () => {
    try {
      const { data } = await api.get('/ads');
      setAds(data.ads || []);
    } catch {
      // ignore
    } finally {
      setLoading(false);
    }
  };

  const fetchBusinessProfiles = async () => {
    try {
      const { data } = await api.get('/business-profiles');
      setBusinessInfo({
        profiles: data.profiles || [],
        limit: data.limit ?? 1,
        used: data.used ?? 0,
      });
      // First-login gate: if the user has no profile AND no saved ads,
      // force them through the onboarding flow before they can do anything.
      // Existing users with ads get an auto-seeded profile from the backend
      // GET, so they'll never trigger this redirect.
      if ((data.profiles || []).length === 0) {
        navigate('/onboarding/business', { replace: true });
      }
    } catch {
      /* best-effort */
    }
  };

  const fetchTrialUsage = async () => {
    try {
      const { data } = await api.get('/billing/usage');
      setTrialUsage(data);
    } catch {
      /* best-effort */
    }
  };


  useEffect(() => {
    fetchAds();
    fetchBusinessProfiles();
    fetchTrialUsage();
    /* eslint-disable-next-line */
  }, []);

  const handleDelete = async (id) => {
    try {
      await api.delete(`/ads/${id}`);
      setAds(prev => prev.filter(a => a.id !== id));
      toast({ title: 'Ad deleted' });
    } catch {
      toast({ title: 'Could not delete', description: 'Please try again.' });
    }
  };

  const handleCopy = (ad) => {
    const text = `${ad.headline}\n\n${ad.body}\n\n${ad.hashtags}`;
    navigator.clipboard.writeText(text);
    toast({ title: 'Copied to clipboard' });
  };

  const handleQuickDownload = async (ad) => {
    if (!ad.image) {
      toast({ title: 'No image', description: 'This ad has no image attached.' });
      return;
    }
    try {
      const res = await fetch(ad.image);
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url; a.download = `mbap-ad-${Date.now()}.png`;
      document.body.appendChild(a); a.click(); a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 1000);
      toast({ title: 'Image downloaded' });
    } catch {
      toast({ title: 'Download failed', description: 'Open the ad and try again.' });
    }
  };

  const filtered = ads.filter(a =>
    !search ||
    a.headline?.toLowerCase().includes(search.toLowerCase()) ||
    a.businessName?.toLowerCase().includes(search.toLowerCase())
  );

  return (
    <div className="min-h-screen bg-[#fdfaf6]">
      <Navbar />

      <div className="max-w-7xl mx-auto px-5 lg:px-8 py-8">
        {/* Header */}
        <div className="flex items-start justify-between flex-wrap gap-4">
          <div>
            <h1 className="text-3xl lg:text-4xl font-extrabold text-slate-900 tracking-tight">
              {user?.name ? `Welcome back, ${user.name.split(' ')[0]}` : 'Your dashboard'} 👋
            </h1>
            <p className="mt-1 text-slate-600">Manage your saved ads and create new ones.</p>
          </div>
          <Button
            onClick={() => navigate('/create')}
            className="bg-gradient-to-r from-orange-500 to-rose-500 hover:from-orange-400 hover:to-rose-400 text-white font-semibold h-11 rounded-xl px-5"
          >
            <Plus className="w-4 h-4 mr-1.5" /> Create new ad
          </Button>
        </div>

        {user?.plan && user.plan !== 'starter' && user.plan !== 'free' && (
          <div className="mt-4 flex items-center justify-between flex-wrap gap-3 bg-gradient-to-r from-orange-50 to-rose-50 border border-orange-200 rounded-2xl p-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-xl bg-white flex items-center justify-center">
                <Sparkles className="w-5 h-5 text-orange-500" />
              </div>
              <div>
                <div className="font-semibold text-slate-900 capitalize">{user.plan} plan</div>
                <div className="text-xs text-slate-500">Manage billing, payment method, or cancel anytime.</div>
              </div>
            </div>
            <Button
              variant="outline"
              onClick={async () => {
                try {
                  const { data } = await api.post('/billing/customer-portal', { returnUrl: window.location.href });
                  window.location.href = data.url;
                } catch (err) {
                  toast({ title: 'Could not open portal', description: err?.response?.data?.detail || 'Try again.' });
                }
              }}
            >
              Manage subscription
            </Button>
          </div>
        )}

        {/* Card-free trial banner — shows X/3 ads + Y/3 images + days left */}
        {trialUsage?.isTrial && (
          <div
            data-testid="trial-usage-banner"
            className="mt-4 flex items-center justify-between flex-wrap gap-4 bg-gradient-to-r from-emerald-50 to-teal-50 border border-emerald-200 rounded-2xl p-4"
          >
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-xl bg-white flex items-center justify-center">
                <ShieldCheck className="w-5 h-5 text-emerald-600" />
              </div>
              <div>
                <div className="font-semibold text-slate-900">
                  7-day free trial · {trialUsage.trialDaysLeft ?? 7} day{(trialUsage.trialDaysLeft ?? 7) === 1 ? '' : 's'} left
                </div>
                <div className="text-xs text-slate-600 mt-0.5">
                  <span data-testid="trial-ads-used">
                    {trialUsage.trialAdsUsed ?? 0} / {trialUsage.trialAdLimit ?? 3} ads
                  </span>
                  {' · '}
                  <span data-testid="trial-images-used">
                    {trialUsage.trialImagesUsed ?? 0} / {trialUsage.trialImageLimit ?? 3} images
                  </span>
                  {' used. No card needed — cancel anytime.'}
                </div>
              </div>
            </div>
            <Button
              data-testid="trial-upgrade-btn"
              variant="outline"
              onClick={() => navigate('/choose-plan')}
              className="border-emerald-300 text-emerald-700 hover:bg-emerald-100"
            >
              Upgrade for unlimited
            </Button>
          </div>
        )}

        {/* Quick actions */}
        <div className="mt-6 grid sm:grid-cols-2 lg:grid-cols-4 gap-3">
          {[
            { t: 'Create AI ad', d: 'Brief in, scroll-stopping ad out.', icon: Sparkles, to: '/create', accent: 'from-orange-100 to-rose-100', iconColor: 'text-orange-600' },
            { t: 'Reply to a review', d: 'Paste any review, get on-brand reply.', icon: MessageSquare, to: '/reviews', accent: 'from-amber-100 to-orange-100', iconColor: 'text-amber-600' },
            { t: 'How to post on socials', d: 'Meta, Instagram, Google Business guides.', icon: Share2, to: '/share-guide', accent: 'from-emerald-100 to-teal-100', iconColor: 'text-emerald-600' },
            { t: 'Google Business Profile', d: 'Manage listing, photos & reviews.', icon: Building2, to: '/gbp', accent: 'from-blue-100 to-indigo-100', iconColor: 'text-blue-600' },
          ].map((a, i) => (
            <button
              key={i}
              onClick={() => navigate(a.to)}
              className="group bg-white border border-slate-200 hover:border-slate-300 rounded-2xl p-5 text-left transition-all hover:shadow-md"
            >
              <div className={`w-11 h-11 rounded-xl bg-gradient-to-br ${a.accent} flex items-center justify-center group-hover:scale-105 transition-transform`}>
                <a.icon className={`w-5 h-5 ${a.iconColor}`} />
              </div>
              <div className="mt-3 font-bold text-slate-900">{a.t}</div>
              <div className="text-sm text-slate-500 mt-0.5">{a.d}</div>
            </button>
          ))}
        </div>

        {/* Stats */}
        <div className="mt-6 grid grid-cols-2 md:grid-cols-4 gap-3">
          {[
            { label: 'Saved ads', value: ads.length, icon: Sparkles, bg: 'bg-orange-100', text: 'text-orange-600' },
            { label: 'This month', value: ads.length, icon: Calendar, bg: 'bg-rose-100', text: 'text-rose-600' },
            { label: 'Compliance pass rate', value: '100%', icon: ShieldCheck, bg: 'bg-emerald-100', text: 'text-emerald-600' },
            { label: 'Avg. engagement boost', value: '+34%', icon: TrendingUp, bg: 'bg-amber-100', text: 'text-amber-600' },
          ].map((s, i) => (
            <div key={i} className="bg-white border border-slate-200 rounded-xl p-4">
              <div className={`w-9 h-9 rounded-lg ${s.bg} flex items-center justify-center`}>
                <s.icon className={`w-4 h-4 ${s.text}`} />
              </div>
              <div className="mt-3 text-2xl font-extrabold text-slate-900">{s.value}</div>
              <div className="text-xs text-slate-500">{s.label}</div>
            </div>
          ))}
        </div>

        {/* Search */}
        <div className="mt-8 flex items-center justify-between flex-wrap gap-3">
          <h2 className="text-xl font-bold text-slate-900">Your saved ads</h2>
          <div className="relative w-full sm:w-72">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
            <Input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search by headline or business…" className="pl-10 h-10" />
          </div>
        </div>

        {/* Empty state */}
        {filtered.length === 0 && (
          <div className="mt-8 bg-white border-2 border-dashed border-slate-200 rounded-2xl p-12 text-center">
            <div className="w-16 h-16 mx-auto rounded-2xl bg-gradient-to-br from-orange-100 to-rose-100 flex items-center justify-center">
              <Sparkles className="w-8 h-8 text-orange-500" />
            </div>
            <h3 className="mt-4 text-xl font-bold text-slate-900">
              {ads.length === 0 ? 'No ads yet' : 'No matches'}
            </h3>
            <p className="mt-1 text-slate-500">
              {ads.length === 0 ? 'Create your first AI-powered ad in 30 seconds.' : 'Try a different search.'}
            </p>
            {ads.length === 0 && (
              <Button onClick={() => navigate('/create')} className="mt-5 bg-slate-900 hover:bg-slate-800 text-white">
                <Plus className="w-4 h-4 mr-1.5" /> Create your first ad
              </Button>
            )}
          </div>
        )}

        {/* Grid */}
        {filtered.length > 0 && (
          <div className="mt-6 grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {filtered.map(ad => (
              <div
                key={ad.id}
                onClick={() => setOpenAd(ad)}
                onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); setOpenAd(ad); } }}
                role="button"
                tabIndex={0}
                aria-label={`Open ad ${ad.headline || 'untitled'}`}
                data-testid={`saved-ad-card-${ad.id}`}
                className="bg-white border border-slate-200 rounded-2xl overflow-hidden hover:shadow-lg hover:border-slate-300 transition-all group cursor-pointer relative focus:outline-none focus:ring-2 focus:ring-orange-500"
              >
                {/* Hover quick-actions */}
                <div className="absolute top-2 right-2 z-10 flex items-center gap-1 opacity-0 group-hover:opacity-100 group-focus-within:opacity-100 transition-opacity">
                  <button
                    onClick={(e) => { e.stopPropagation(); handleCopy(ad); }}
                    className="p-2 rounded-full bg-white/95 hover:bg-white shadow text-slate-600 hover:text-slate-900"
                    title="Copy text"
                    aria-label="Copy text"
                    data-testid={`saved-ad-quick-copy-${ad.id}`}
                  >
                    <Copy className="w-4 h-4" />
                  </button>
                  <button
                    onClick={(e) => { e.stopPropagation(); handleQuickDownload(ad); }}
                    className="p-2 rounded-full bg-white/95 hover:bg-white shadow text-slate-600 hover:text-slate-900"
                    title="Download image"
                    aria-label="Download image"
                    data-testid={`saved-ad-quick-download-${ad.id}`}
                  >
                    <Download className="w-4 h-4" />
                  </button>
                </div>

                {ad.image ? (
                  <img src={ad.image} alt="" className="w-full h-40 object-cover" />
                ) : (
                  <div className="w-full h-40 bg-gradient-to-br from-orange-100 to-rose-100 flex items-center justify-center">
                    <ImageIcon className="w-10 h-10 text-orange-300" />
                  </div>
                )}
                <div className="p-4">
                  <div className="flex items-start justify-between gap-2">
                    <Badge variant="outline" className="text-xs border-slate-200 text-slate-600">
                      {ad.businessType || 'business'}
                    </Badge>
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <button
                          onClick={(e) => e.stopPropagation()}
                          className="text-slate-400 hover:text-slate-700 p-1"
                          data-testid={`saved-ad-menu-${ad.id}`}
                        >
                          <MoreVertical className="w-4 h-4" />
                        </button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="end" onClick={(e) => e.stopPropagation()}>
                        <DropdownMenuItem onClick={() => setOpenAd(ad)}>
                          <Sparkles className="w-4 h-4 mr-2" /> Open ad
                        </DropdownMenuItem>
                        <DropdownMenuItem onClick={() => handleCopy(ad)}>
                          <Copy className="w-4 h-4 mr-2" /> Copy text
                        </DropdownMenuItem>
                        <DropdownMenuItem onClick={() => handleQuickDownload(ad)}>
                          <Download className="w-4 h-4 mr-2" /> Download image
                        </DropdownMenuItem>
                        <DropdownMenuItem onClick={() => navigate(`/create?adId=${ad.id}`)}>
                          <Sparkles className="w-4 h-4 mr-2" /> Open in Create
                        </DropdownMenuItem>
                        <DropdownMenuItem onClick={() => handleDelete(ad.id)} className="text-rose-600">
                          <Trash2 className="w-4 h-4 mr-2" /> Delete
                        </DropdownMenuItem>
                      </DropdownMenuContent>
                    </DropdownMenu>
                  </div>
                  <h3 className="mt-2 font-bold text-slate-900 line-clamp-2">{ad.headline}</h3>
                  <p className="mt-1 text-sm text-slate-600 line-clamp-2">{ad.body}</p>
                  <div className="mt-3 flex items-center justify-between">
                    <span className="text-xs text-slate-500">{ad.businessName || 'Untitled'}</span>
                    <span className="text-xs text-emerald-600 font-medium flex items-center gap-1">
                      <ShieldCheck className="w-3 h-3" /> Compliant
                    </span>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Saved-ad detail modal */}
      {openAd && (
        <AdDetailModal
          ad={openAd}
          onClose={() => setOpenAd(null)}
          onUpdate={(updated) => {
            setAds(prev => prev.map(a => (a.id === updated.id ? { ...a, ...updated } : a)));
            setOpenAd(updated);
          }}
          onDelete={(id) => setAds(prev => prev.filter(a => a.id !== id))}
        />
      )}
    </div>
  );
};

export default Dashboard;
